(() => {
  if (customElements.get('lm-featured-collection')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const desktopMQ = window.matchMedia('(min-width: 990px)');

  class LmFeaturedCollection extends HTMLElement {
    connectedCallback() {
      this.panels = [...this.querySelectorAll('.lmfc__panel')];
      if (!this.panels.length) return;
      this.isRTL = getComputedStyle(this).direction === 'rtl';
      this.prev = this.querySelector('.lmfc__arrow[data-dir="-1"]');
      this.next = this.querySelector('.lmfc__arrow[data-dir="1"]');
      this.viewAll = [...this.querySelectorAll('[data-view-all]')];

      this.onMQ = () => { this.update(); this.restartAutoplay(); };
      desktopMQ.addEventListener('change', this.onMQ);
      this.onResize = () => this.update();
      window.addEventListener('resize', this.onResize, { passive: true });

      this.initReveal();
      this.initTabs();
      this.initFilters();
      this.initShowMore();
      this.initQuantity();
      this.initQuickAdd();
      if (canHover && !reduceMotion) this.initPointerEffects();
      this.initCarousel();
    }

    disconnectedCallback() {
      clearInterval(this.autoplayTimer);
      this.observer?.disconnect();
      desktopMQ.removeEventListener('change', this.onMQ);
      window.removeEventListener('resize', this.onResize);
    }

    get panel() { return this.panels.find((p) => !p.hidden) || this.panels[0]; }
    get track() { return this.panel.querySelector('.lmfc__track'); }
    isCarousel() { return (desktopMQ.matches ? this.dataset.d : this.dataset.m) === 'carousel'; }

    initReveal() {
      if (this.dataset.reveal === 'none' || reduceMotion || !('IntersectionObserver' in window) || (window.Shopify && Shopify.designMode)) {
        this.classList.add('is-visible');
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.classList.add('is-visible');
          this.observer.disconnect();
        }
      }, { threshold: 0.12 });
      this.observer.observe(this);
    }

    initTabs() {
      const tabs = [...this.querySelectorAll('.lmfc__tab')];
      if (!tabs.length) return;
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
        });
        const target = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
        this.panels.forEach((p) => {
          p.hidden = p !== target;
        });
        if (target) {
          target.classList.remove('is-entering');
          void target.offsetWidth;
          if (!reduceMotion) target.classList.add('is-entering');
          const url = target.dataset.url;
          this.viewAll.forEach((a) => {
            if (url) { a.href = url; a.hidden = false; } else { a.hidden = true; }
          });
          const tr = target.querySelector('.lmfc__track');
          if (tr) tr.scrollLeft = 0;
        }
        tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
        if (focus) tab.focus();
        this.update();
        this.restartAutoplay();
      };
      tabs.forEach((tab, i) => {
        tab.tabIndex = tab.getAttribute('aria-selected') === 'true' ? 0 : -1;
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (e) => {
          const fwd = this.isRTL ? 'ArrowLeft' : 'ArrowRight';
          const back = this.isRTL ? 'ArrowRight' : 'ArrowLeft';
          let n = null;
          if (e.key === fwd) n = tabs[(i + 1) % tabs.length];
          if (e.key === back) n = tabs[(i - 1 + tabs.length) % tabs.length];
          if (e.key === 'Home') n = tabs[0];
          if (e.key === 'End') n = tabs[tabs.length - 1];
          if (n) { e.preventDefault(); select(n, true); }
        });
      });
    }

    initFilters() {
      this.panels.forEach((panel) => {
        const chips = panel.querySelectorAll('.lmfc__chip');
        chips.forEach((chip) => {
          chip.addEventListener('click', () => {
            const f = chip.dataset.filter;
            chips.forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
            panel.querySelectorAll('.lmfc__item').forEach((item) => {
              item.hidden = !(f === '*' || item.dataset.type === f);
            });
            if (f !== '*') panel.classList.add('is-expanded');
            const tr = panel.querySelector('.lmfc__track');
            if (tr) tr.scrollLeft = 0;
            this.update();
          });
        });
      });
    }

    initShowMore() {
      this.panels.forEach((panel) => {
        const btn = panel.querySelector('[data-show-more]');
        if (!btn) return;
        btn.addEventListener('click', () => {
          const open = panel.classList.toggle('is-expanded');
          btn.setAttribute('aria-expanded', String(open));
          btn.querySelector('[data-more-label]').hidden = open;
          btn.querySelector('[data-less-label]').hidden = !open;
          if (!open) panel.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      });
    }

    initQuantity() {
      this.querySelectorAll('.lmfc-qty').forEach((wrap) => {
        const input = wrap.querySelector('input');
        wrap.querySelectorAll('[data-step]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const step = Number(input.step) || 1;
            const min = Number(input.min) || 1;
            const max = input.max ? Number(input.max) : Infinity;
            const next = (Number(input.value) || min) + step * Number(btn.dataset.step);
            input.value = Math.min(max, Math.max(min, next));
          });
        });
      });
    }

    initQuickAdd() {
      this.querySelectorAll('form[data-quick-add]').forEach((form) => {
        form.addEventListener('submit', async (evt) => {
          evt.preventDefault();
          const btn = form.querySelector('button[type="submit"]');
          const label = btn.querySelector('.lmfc-btn__label');
          const original = label.textContent;
          form.parentElement.querySelector('.lmfc-qa__error')?.remove();
          btn.classList.add('is-loading');
          btn.disabled = true;

          const notification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          const body = new FormData(form);
          if (notification && typeof notification.getSectionsToRender === 'function') {
            body.append('sections', notification.getSectionsToRender().map((s) => s.id));
            body.append('sections_url', window.location.pathname);
            notification.setActiveElement?.(btn);
          } else {
            body.append('sections', 'cart-icon-bubble');
          }

          try {
            const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
              method: 'POST',
              headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body,
            });
            const data = await res.json();
            if (!res.ok || data.status) throw new Error(data.description || data.message || 'Error');

            if (notification && typeof notification.renderContents === 'function') {
              notification.renderContents(data);
            } else if (data.sections && data.sections['cart-icon-bubble']) {
              const bubble = document.getElementById('cart-icon-bubble');
              const html = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
              const fresh = html.querySelector('#cart-icon-bubble') || html.querySelector('.shopify-section');
              if (bubble && fresh) bubble.innerHTML = fresh.innerHTML;
            }
            if (window.publish && window.PUB_SUB_EVENTS) {
              window.publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lm-featured-collection', productVariantId: body.get('id'), cartData: data });
            }

            btn.classList.remove('is-loading');
            btn.classList.add('is-added');
            label.textContent = this.dataset.addedLabel || original;
            setTimeout(() => {
              btn.classList.remove('is-added');
              label.textContent = original;
              btn.disabled = false;
            }, 1800);
          } catch (err) {
            btn.classList.remove('is-loading');
            btn.disabled = false;
            const p = document.createElement('p');
            p.className = 'lmfc-qa__error';
            p.setAttribute('role', 'alert');
            p.textContent = err.message;
            form.after(p);
          }
        });
      });
    }

    initPointerEffects() {
      const maxTilt = Number(this.dataset.tilt) || 0;
      const spotlight = this.classList.contains('lmfc--spotlight');
      if (!maxTilt && !spotlight) return;
      this.querySelectorAll('[data-card]').forEach((card) => {
        let frame;
        card.addEventListener('pointermove', (e) => {
          if (e.pointerType !== 'mouse') return;
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => {
            const r = card.getBoundingClientRect();
            const x = (e.clientX - r.left) / r.width;
            const y = (e.clientY - r.top) / r.height;
            card.style.setProperty('--mx', `${x * 100}%`);
            card.style.setProperty('--my', `${y * 100}%`);
            if (maxTilt) {
              card.classList.add('is-tilting');
              card.style.setProperty('--ry', `${(x - 0.5) * maxTilt * 2}deg`);
              card.style.setProperty('--rx', `${(0.5 - y) * maxTilt * 2}deg`);
            }
          });
        });
        card.addEventListener('pointerleave', () => {
          cancelAnimationFrame(frame);
          card.classList.remove('is-tilting');
          card.style.setProperty('--rx', '0deg');
          card.style.setProperty('--ry', '0deg');
        });
      });
    }

    step() {
      const tr = this.track;
      const item = tr.querySelector('.lmfc__item:not([hidden])');
      const gap = parseFloat(getComputedStyle(tr).columnGap) || 0;
      return item ? item.getBoundingClientRect().width + gap : tr.clientWidth;
    }

    go(dir) {
      const logical = this.isRTL ? -dir : dir;
      this.track.scrollBy({ left: this.step() * logical, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    initCarousel() {
      [this.prev, this.next].forEach((btn) => btn?.addEventListener('click', () => {
        this.go(Number(btn.dataset.dir));
        this.restartAutoplay();
      }));

      this.panels.forEach((panel) => {
        const tr = panel.querySelector('.lmfc__track');
        if (!tr) return;
        tr.addEventListener('scroll', () => { if (!panel.hidden) this.update(); }, { passive: true });
        tr.addEventListener('keydown', (e) => {
          if (!this.isCarousel()) return;
          if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.isRTL ? -1 : 1); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.isRTL ? 1 : -1); }
        });
        if (this.dataset.drag === 'true' && canHover) this.initDrag(tr);
      });

      this.autoplayMs = Number(this.dataset.autoplay) || 0;
      if (this.autoplayMs && !reduceMotion) {
        this.addEventListener('mouseenter', () => clearInterval(this.autoplayTimer));
        this.addEventListener('mouseleave', () => this.restartAutoplay());
        this.addEventListener('focusin', () => clearInterval(this.autoplayTimer));
        this.addEventListener('touchstart', () => clearInterval(this.autoplayTimer), { passive: true });
      }
      this.update();
      this.restartAutoplay();
    }

    restartAutoplay() {
      clearInterval(this.autoplayTimer);
      if (!this.autoplayMs || reduceMotion || !this.isCarousel()) return;
      this.autoplayTimer = setInterval(() => {
        if (document.hidden) return;
        const { max, pos } = this.metrics();
        if (pos >= max - 4) this.track.scrollTo({ left: 0, behavior: 'smooth' });
        else this.go(1);
      }, this.autoplayMs);
    }

    metrics() {
      const tr = this.track;
      return { max: tr.scrollWidth - tr.clientWidth, pos: Math.abs(tr.scrollLeft), tr };
    }

    update() {
      if (!this.panel) return;
      const { max, pos, tr } = this.metrics();
      const scrollable = this.isCarousel() && max > 4;
      if (this.prev) this.prev.disabled = !scrollable || pos <= 4;
      if (this.next) this.next.disabled = !scrollable || pos >= max - 4;
      const bar = this.panel.querySelector('.lmfc__progress-bar');
      const progress = this.panel.querySelector('.lmfc__progress');
      if (bar && progress) {
        progress.style.visibility = scrollable ? '' : 'hidden';
        const width = Math.max(8, Math.min(100, (tr.clientWidth / tr.scrollWidth) * 100));
        const offset = max > 0 ? (pos / max) * (100 - width) : 0;
        bar.style.width = `${width}%`;
        bar.style.transform = `translateX(${(this.isRTL ? -1 : 1) * (offset / width) * 100}%)`;
      }
    }

    initDrag(tr) {
      let startX = 0;
      let startLeft = 0;
      let dragging = false;
      let moved = false;
      tr.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || !this.isCarousel() || e.target.closest('button, input, form, select')) return;
        dragging = true;
        moved = false;
        startX = e.clientX;
        startLeft = tr.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 5) {
          moved = true;
          tr.classList.add('is-dragging');
        }
        if (moved) tr.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!dragging) return;
        dragging = false;
        tr.classList.remove('is-dragging');
      });
      tr.addEventListener('click', (e) => {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        }
      }, true);
    }
  }

  customElements.define('lm-featured-collection', LmFeaturedCollection);
})();
